import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight, Check, X } from 'lucide-react'
import { Button } from '@/app/components/ui/Button'
import { readPhoto } from '@/app/lib/local/watcher'
import { cn } from '@/app/lib/cn'
import type { ImageFile } from '@/core/types'

/**
 * A photo at full resolution, over the gallery.
 *
 * The grid shows 400px thumbnails, which is the right size for picking a photo
 * out of several hundred and the wrong size for the decision the operator is
 * actually making — is anyone blinking, is it sharp, is that the shot the guest
 * asked for. This reads the original file rather than scaling the thumbnail up,
 * so it answers that.
 *
 * Selection is available from in here because checking a photo and choosing it
 * are the same act; closing the preview to go and tap the tile is a step for
 * nothing. The numbered badge still comes from the grid — this shows the
 * position if it has one.
 */
export function PhotoPreview({
  file,
  order,
  selected,
  onToggleSelect,
  onPrev,
  onNext,
  onClose,
}: {
  file: ImageFile
  /** 1-based position in the selection, when selected. */
  order?: number
  selected: boolean
  onToggleSelect: () => void
  onPrev?: () => void
  onNext?: () => void
  onClose: () => void
}) {
  const [url, setUrl] = useState<string>()
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | undefined

    void (async () => {
      setUrl(undefined)
      setFailed(false)

      const source = await readPhoto(file)
      // The operator moved or deleted it since the grid was built — an ordinary
      // event on a floor where someone is tidying the camera folder.
      if (!source) {
        if (!cancelled) setFailed(true)
        return
      }
      if (cancelled) return

      objectUrl = URL.createObjectURL(source)
      setUrl(objectUrl)
    })()

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [file])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowLeft') onPrev?.()
      if (event.key === 'ArrowRight') onNext?.()
      // Space is the natural "pick this one" once a photo fills the screen.
      if (event.key === ' ') {
        event.preventDefault()
        onToggleSelect()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, onPrev, onNext, onToggleSelect])

  // Into <body>, not where it is written. The step wrapper animates with
  // `fill-mode: both`, and the transform it keeps afterwards makes it the
  // containing block for `position: fixed` — so `inset-0` would cover the step
  // rather than the screen, shifted down and cut off at the bottom.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={file.fileName}
      // Clicking the backdrop closes; clicking the photo itself must not.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
      className="fixed inset-0 z-50 flex flex-col bg-black/85 backdrop-blur-sm"
    >
      <header className="flex shrink-0 items-center gap-3 px-5 py-3">
        <span className="min-w-0 flex-1 truncate text-[15px] text-white" title={file.fileName}>
          {file.fileName}
        </span>
        {order !== undefined && (
          <span className="grid h-8 min-w-[32px] shrink-0 place-items-center rounded-full bg-brand px-2 text-[14px] font-bold text-on-brand">
            {order}
          </span>
        )}
        <Button variant="ghost" aria-label="Tutup" onClick={onClose} className="w-[48px] px-0">
          <X size={22} />
        </Button>
      </header>

      <div
        className="relative flex min-h-0 flex-1 items-center justify-center px-3"
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose()
        }}
      >
        {onPrev && (
          <Button
            variant="secondary"
            aria-label="Foto sebelumnya"
            onClick={onPrev}
            className="absolute left-4 z-10 h-[56px] w-[56px] rounded-full px-0"
          >
            <ChevronLeft size={26} />
          </Button>
        )}

        {failed ? (
          <p className="text-[15px] text-white/70">Foto ini tidak bisa dibuka lagi.</p>
        ) : url ? (
          <img
            src={url}
            alt={file.fileName}
            className="max-h-full max-w-full object-contain"
            draggable={false}
          />
        ) : (
          <p className="citaria-live text-[15px] text-white/70">Memuat foto…</p>
        )}

        {onNext && (
          <Button
            variant="secondary"
            aria-label="Foto berikutnya"
            onClick={onNext}
            className="absolute right-4 z-10 h-[56px] w-[56px] rounded-full px-0"
          >
            <ChevronRight size={26} />
          </Button>
        )}
      </div>

      <footer className="flex shrink-0 items-center justify-center gap-3 px-5 py-4">
        <Button
          size="lg"
          variant={selected ? 'secondary' : 'primary'}
          onClick={onToggleSelect}
          className={cn('min-w-[220px]', selected && 'ring-1 ring-brand')}
        >
          {selected ? <X size={18} /> : <Check size={18} />}
          {selected ? 'Batal pilih' : 'Pilih foto ini'}
        </Button>
      </footer>
    </div>,
    document.body,
  )
}
